import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

const messages = defineMessages({
	agentMentionNudgeTitle: {
		id: 'page-rovo-config-common.agent-mention-nudge-title',
		defaultMessage: 'Start chatting with agent',
		description:
			'Title for the agent mention nudge that appears next to the Rovo button when a user inserts an agent mention in the document',
	},
	agentMentionNudgeIconLabel: {
		id: 'page-rovo-config-common.agent-mention-nudge-icon-label',
		defaultMessage: 'Agent icon',
		description: 'Accessible label for the icon in the agent mention nudge',
	},
	agentMentionNudgeSecondaryTitle: {
		id: 'page-rovo-config-common.agent-mention-nudge-secondary-title',
		defaultMessage: 'Start a conversation using this agent',
		description: 'Subtitle shown below the agent mention nudge title on hover',
	},
});

export const AGENT_MENTION_NUDGE_ID = 'agent-mention-nudge';

/** ProseMirror node type for task list items. */
export const TASK_ITEM_NODE_TYPE = 'taskItem';

/** Defined outside the hook so React keeps the same DOM node across re-renders. */
const AgentMentionIcon = ({ label }: { label: string }) => (
	<PersonAvatarIcon size="small" label={label} />
);

/**
 * Hook that creates the agent mention nudge action.
 *
 * @param agentId - Resolved Agent Studio UUID for the mentioned agent (null if unresolved).
 * @param agentMentionContext - Plain-text content of the block containing the mention.
 * @param agentMentionParentNodeType - ProseMirror node type of the mention's parent block.
 * @param agentMentionInsertionCount - Monotonic count for agent mention insertions on the page.
 */
export function useAgentMentionNudgeAction(
	agentId?: string | null,
	agentMentionContext?: string | null,
	agentMentionParentNodeType?: string | null,
	agentMentionInsertionCount?: number,
): ConversationAction {
	const resolvedAgentId = agentId ?? null;
	const resolvedPrompt = agentMentionContext ?? undefined;
	const isTaskItem = agentMentionParentNodeType === TASK_ITEM_NODE_TYPE;
	const nudgeId =
		agentMentionInsertionCount && agentMentionInsertionCount > 0
			? `${AGENT_MENTION_NUDGE_ID}-${agentMentionInsertionCount}`
			: AGENT_MENTION_NUDGE_ID;
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	// Keep a ref to the latest prompt so onClick doesn't need it as a dep.
	// agentMentionContext changes on every keystroke; without this, onClick would
	// recreate on each keystroke → cascading memo invalidations → setNudges on every keystroke.
	const resolvedPromptRef = useRef(resolvedPrompt);
	resolvedPromptRef.current = resolvedPrompt;

	// Keep a ref to the latest agentId so onClick doesn't capture a stale null.
	// agentId resolves asynchronously; without this, the callback created before
	// resolution would permanently close over null.
	const resolvedAgentIdRef = useRef(resolvedAgentId);
	resolvedAgentIdRef.current = resolvedAgentId;

	const iconLabel = formatMessage(messages.agentMentionNudgeIconLabel);

	// Stable as long as agent identity and node type don't change.
	const onClick = useCallback(() => {
		publish({
			type: 'chat-new',
			source: 'agentMentionEditorNudge',
			openChatMode: 'mini-modal',
			data: {
				prompt: resolvedPromptRef.current,
				// Task items auto-send; other blocks pre-fill as a draft.
				isPromptPlaceholder: !isTaskItem,
				...(!isTaskItem ? { placeholderType: 'generic' } : {}),
				dialogues: [],
				mode: { useCurrentPageContext: true },
				...(resolvedAgentIdRef.current ? { agentId: resolvedAgentIdRef.current } : {}),
			},
		});
	}, [publish, isTaskItem]);

	return useMemo(
		() => ({
			id: nudgeId,
			title: formatMessage(messages.agentMentionNudgeTitle),
			secondaryTitle: formatMessage(messages.agentMentionNudgeSecondaryTitle),
			icon: () => <AgentMentionIcon label={iconLabel} />,
			onClick,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: AGENT_MENTION_NUDGE_ID,
				selfEligible: true,
			},
		}),
		[formatMessage, iconLabel, nudgeId, onClick],
	);
}
